import { E2EKeyApi } from '@/shared/api/methods/E2EKeyApi'
import { openDB, PEERS_STORE } from './keyStore'

/**
 * In-memory cache of peers' public keys, keyed by user id. Fetched lazily from
 * the server the first time a secret chat with that peer needs crypto.
 */
const cache = new Map<number, string>()
const inflight = new Map<number, Promise<string | null>>()

/** Resolve a peer's base64 public key (cached). Null if the peer has no E2E key. */
export async function getPeerPublicKey(userId: number): Promise<string | null> {
  const cached = cache.get(userId)
  if (cached) return cached

  const existing = inflight.get(userId)
  if (existing) return existing

  const p = E2EKeyApi.getUserKey(userId)
    .then((res) => {
      if (res && res.public_key) {
        cache.set(userId, res.public_key)
        return res.public_key
      }
      return null
    })
    .finally(() => inflight.delete(userId))

  inflight.set(userId, p)
  return p
}

/** Overwrite the cached key for a peer (e.g. after detecting a key change). */
export function setPeerPublicKey(userId: number, publicKey: string): void {
  cache.set(userId, publicKey)
}

/** Drop a peer's cached key, forcing a refetch next time. */
export function invalidatePeerKey(userId: number): void {
  cache.delete(userId)
}

// --- Последний виденный ключ собеседника (хранится на устройстве) ---
// Кэш выше живёт только до перезагрузки страницы, а смену ключа нужно замечать
// между сессиями. Поэтому ключ, которому пользователь доверился, запоминаем в
// IndexedDB и сравниваем с тем, что отдаёт сервер (docs/encryption/E2E-CALLS.md, §6).

/** Ключ собеседника, которому мы доверяли в прошлый раз, или null. */
export async function getKnownPeerKey(userId: number): Promise<string | null> {
  try {
    const db = await openDB()
    return await new Promise<string | null>((resolve, reject) => {
      const req = db.transaction(PEERS_STORE, 'readonly').objectStore(PEERS_STORE).get(String(userId))
      req.onsuccess = () => resolve(typeof req.result === 'string' ? req.result : null)
      req.onerror = () => reject(req.error)
    })
  } catch {
    return null
  }
}

/** Запомнить ключ собеседника как доверенный. */
export async function rememberPeerKey(userId: number, publicKey: string): Promise<void> {
  setPeerPublicKey(userId, publicKey)
  try {
    const db = await openDB()
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(PEERS_STORE, 'readwrite')
      tx.objectStore(PEERS_STORE).put(publicKey, String(userId))
      tx.oncomplete = () => resolve()
      tx.onerror = () => reject(tx.error)
    })
  } catch {
    /* хранилище недоступно — проверка смены ключа просто не сработает */
  }
}

export interface FreshPeerKey {
  publicKey: string
  capabilities: string[]
  /** Ключ отличается от того, которому доверяли раньше. */
  changed: boolean
}

/**
 * Ключ собеседника прямо с сервера, мимо кэша. При первом контакте ключ
 * запоминается сразу (доверие при первом контакте); при смене — НЕ
 * запоминается, решение за пользователем (rememberPeerKey).
 */
export async function fetchPeerKeyFresh(userId: number): Promise<FreshPeerKey | null> {
  const res = await E2EKeyApi.getUserKey(userId)
  if (!res || !res.public_key) return null

  const known = await getKnownPeerKey(userId)
  const changed = known !== null && known !== res.public_key
  if (known === null) {
    await rememberPeerKey(userId, res.public_key)
  }

  return {
    publicKey: res.public_key,
    capabilities: Array.isArray(res.capabilities) ? res.capabilities : [],
    changed,
  }
}
